/**
 * Parents dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ParentsDataTable } from '@/components/parents/ParentsDataTable';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { Select } from '@/components/forms/Select';
import { CreateParentView } from '@/components/parents/CreateParentView';
import { EditParentView } from '@/components/parents/EditParentView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { onboardingKeys } from '@/hooks/useUsers';
import {
  PARENT_TAB_LABELS,
  PARENT_TAB_COLORS,
  PARENT_TAB_ICONS,
  type ParentTabView,
} from '@/components/parents/parent-configs';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { ParentRecord } from '@/types/parent.types';

/** Props for {@link ParentsSection}. */
interface ParentsSectionProps {
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: remote parents list. */
export const ParentsSection: React.FC<ParentsSectionProps> = ({ layoutWidth = 'full' }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canArchiveRow, canReactivateRow } = usePageActions('parents');
  const { canCreate: canCreateStudent } = usePageActions('students');
  const { canViewInterface } = usePermissions();
  const canViewStudents = canViewInterface('students');
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [activeTab, setActiveTab] = React.useState<ParentTabView>('parents');
  /** Which form is mounted in the collapse wrapper. */
  const [activeForm, setActiveForm] = React.useState<'create' | 'edit' | null>(null);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [editStepKey, setEditStepKey] = React.useState<string | undefined>(undefined);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  /** Close the form and drop the edit record / step so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
  }, []);

  /** Close the form and refresh onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  }, [queryClient]);

  const handleInviteChild = (_id: string, rec: ParentRecord) => {
    void navigate('/parents', { state: { openParentUserId: rec.userId } });
  };

  const handleViewStudents = (_id: string, rec: ParentRecord) => {
    void navigate('/students', {
      state: {
        linkedUserId: rec.userId,
        personName: `${rec.user?.firstName ?? ''} ${rec.user?.lastName ?? ''}`.trim(),
      },
    });
  };

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  const tabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-parents');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return (Object.keys(PARENT_TAB_LABELS) as ParentTabView[]).map((id) => {
      const Icon = id === 'parents' ? MainIcon : PARENT_TAB_ICONS[id];
      return {
        id,
        label: PARENT_TAB_LABELS[id],
        icon: <Icon className="h-4 w-4" />,
        color: PARENT_TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  const tabsNode = React.useMemo(
    () => (
      <Select
        value={activeTab}
        onChange={(id) => {
          closeForm();
          closeRow();
          setActiveTab(id as typeof activeTab);
        }}
        options={tabItems.map((tab) => ({
          value: tab.id,
          label: tab.label,
          icon: tab.icon,
          color: tab.color,
        }))}
        size="sm"
      />
    ),
    [tabItems, activeTab, closeRow, closeForm],
  );

  /** Stable JSX node for the form — avoids unmounting {@link EditParentView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateParentView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editUserId) {
      return (
        <EditParentView
          key={editUserId}
          userId={editUserId}
          stepKey={editStepKey}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editUserId, editStepKey, handleFormSuccess, closeForm]);

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <ParentsDataTable
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditParent={(id) => {
            setEditUserId(id);
            setEditStepKey('basic_information');
            setActiveForm('edit');
            closeRow();
          }}
          onInviteChild={handleInviteChild}
          onViewStudents={handleViewStudents}
          enableFilters={true}
          isSection={true}
          layoutWidth={layoutWidth}
          activeTab={activeTab}
          pageActions={{
            canEditRow,
            canViewRow: true,
            canInviteChild: canCreateStudent,
            canViewStudents,
            canArchiveRow,
            canReactivateRow,
          }}
          showCreateButton={canCreate}
          onCreateParent={() => {
            setActiveForm('create');
            closeRow();
          }}
          toolbarLeft={tabsNode}
        />
      </CollapseReplaceWrapper>
    </div>
  );
};
